// ConnectRepositoryModal (RFC-0002 Task 3.5).
//
// One shared modal, opened via useConnectRepo(), behind all three "Connect
// repository" controls. Reads context state; has no public props.
//
// Four states:
//   - idle:       repo-name input; submit disabled until the name is valid
//                 (client validation mirrors the server allow-list).
//   - submitting: reuses Button's `loading` prop (no new affordance).
//   - success:    the 202 acknowledgment + a pointer to the manifest contract
//                 (PD-0004 / AD-0001): add a `.nmblr.portal.yaml`.
//   - error:      a 400 (invalid name) is shown INLINE near the input; a
//                 network/5xx failure is shown as a dismissible banner + Retry.

import { useEffect, useState } from 'react';
import { X, GithubLogo, CheckCircle } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConnectRepoResult } from '@portal/shared-types';
import { Button } from '@/components/ui/button';
import { useConnectRepo } from '@/context/connect-repo';
import { validateRepoName } from '@/lib/repo-name';
import { connectRepo, RepoNameError, SyncRequestError } from '@/lib/sync-api';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export function ConnectRepositoryModal() {
  const { isOpen, close } = useConnectRepo();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [result, setResult] = useState<ConnectRepoResult | null>(null);

  // Reset to a clean idle state each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setName('');
      setStatus('idle');
      setInlineError(null);
      setBannerError(null);
      setResult(null);
    }
  }, [isOpen]);

  // Body scroll-lock while open, restoring the previous value on close/unmount —
  // mirrors the Drawer primitive so the page can't scroll behind the modal on a
  // phone. Gated on isOpen because this component stays mounted when closed.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const clientValidation = validateRepoName(name);
  const canSubmit = clientValidation.ok && status !== 'submitting';
  // Live client-side error while typing an invalid non-empty name; the server
  // 400 (inlineError) takes precedence once a request has come back.
  const clientError =
    name.trim().length > 0 && !clientValidation.ok ? clientValidation.message : null;
  const shownInlineError = inlineError ?? clientError;

  async function handleSubmit() {
    const validation = validateRepoName(name);
    if (!validation.ok) {
      setInlineError(validation.message);
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setInlineError(null);
    setBannerError(null);

    try {
      const res = await connectRepo(validation.name);
      setResult(res);
      setStatus('success');
      // A 202 means a new repo will appear once its first sync runs — refresh the
      // read caches so Overview/Repositories/sync-issues reflect it without a
      // manual reload (RFC-0009 Task 1.4).
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['sync-issues'] });
    } catch (err) {
      if (err instanceof RepoNameError) {
        setInlineError(err.message);
        setStatus('error');
      } else if (err instanceof SyncRequestError) {
        setBannerError(err.message);
        setStatus('error');
      } else {
        setBannerError('Something went wrong. Please try again.');
        setStatus('error');
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-repo-title"
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border border-border bg-surface p-5 shadow-[var(--c-shadow-md)] sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id="connect-repo-title" className="text-base font-semibold text-text">
            {status === 'success' ? 'Sync queued' : 'Connect a repository'}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted hover:bg-accent-faint hover:text-text"
          >
            <X size={18} />
          </button>
        </div>

        {status === 'success' && result ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-success">
                <CheckCircle size={22} weight="fill" />
              </span>
              <p className="text-[13px] leading-relaxed text-muted">
                Sync queued for{' '}
                <span className="font-mono text-text">{result.repo}</span>. Add a{' '}
                <span className="font-mono text-text">.nmblr.portal.yaml</span> to that
                repo to control what gets indexed.
              </p>
            </div>
            <div className="flex justify-end">
              <Button size="md" onClick={close}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-[13px] leading-relaxed text-muted">
              Enter a repository name in your GitHub org. We&rsquo;ll queue an
              immediate first sync of its docs.
            </p>

            {bannerError && (
              <div
                role="alert"
                className="flex items-center justify-between gap-3 rounded-lg border border-[rgba(255,92,92,0.2)] bg-error-bg px-3 py-2 text-[13px] text-badge-error-text"
              >
                <span>{bannerError}</span>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() => setBannerError(null)}
                  className="shrink-0 text-current/70 hover:text-current"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="connect-repo-name" className="text-xs font-medium text-text">
                Repository name
              </label>
              <input
                id="connect-repo-name"
                type="text"
                value={name}
                autoFocus
                placeholder="payments-service"
                aria-invalid={shownInlineError ? true : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  if (inlineError) setInlineError(null);
                  if (status === 'error') setStatus('idle');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmit) handleSubmit();
                }}
                className="rounded-lg border border-border bg-canvas px-3 py-2 font-mono text-[13px] text-text outline-none focus:border-accent"
              />
              {shownInlineError && (
                <p role="alert" className="text-xs text-badge-error-text">
                  {shownInlineError}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="md" onClick={close}>
                Cancel
              </Button>
              <Button
                size="md"
                loading={status === 'submitting'}
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                <GithubLogo size={16} />
                {bannerError ? 'Retry' : 'Connect'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
